import { useEffect, useState } from "react";
import api from "../api";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const EURk = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(Math.abs(v) >= 10000 ? 0 : 1)}k €` : `${Math.round(v)} €`);
const fmtDT = (v) => (v ? new Date(v).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function Chart({ chart, me }) {
  const W = 760, H = 250, PL = 52, PR = 10, PT = 14, PB = 26;
  const rev = [...chart.actual_rev, ...chart.forecast_rev];
  const marg = [...chart.actual_margin, ...chart.forecast_margin];
  const all = [...rev, ...marg, 0];
  const lo = Math.min(...all), hi = Math.max(...all);
  const span = (hi - lo) || 1;
  const x = (i) => PL + (i * (W - PL - PR)) / 11;
  const y = (v) => PT + (H - PT - PB) * (1 - (v - lo) / span);
  const path = (arr, from, to) => arr.slice(from, to + 1).map((v, i) => `${i === 0 ? "M" : "L"}${x(from + i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const splitX = x(me - 1);
  const grid = [0, 0.25, 0.5, 0.75, 1].map((t) => lo + t * span);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }} data-testid="margin-forecast-chart">
      {grid.map((v, i) => (
        <g key={i}>
          <line x1={PL} x2={W - PR} y1={y(v)} y2={y(v)} stroke="rgba(148,163,184,.15)" strokeWidth="1" />
          <text x={PL - 6} y={y(v) + 3} textAnchor="end" fontSize="10" fill="#94a3b8">{EURk(v)}</text>
        </g>
      ))}
      {lo < 0 && <line x1={PL} x2={W - PR} y1={y(0)} y2={y(0)} stroke="rgba(251,113,133,.4)" strokeWidth="1" />}
      {/* zona previsione */}
      <rect x={splitX} y={PT} width={x(11) - splitX} height={H - PT - PB} fill="rgba(96,165,250,.06)" />
      <line x1={splitX} x2={splitX} y1={PT} y2={H - PB} stroke="rgba(148,163,184,.5)" strokeDasharray="3 3" />
      <text x={splitX + 6} y={PT + 10} fontSize="10" fill="#94a3b8">previsione →</text>
      {/* fatturato */}
      <path d={path(rev, 0, me - 1)} fill="none" stroke="#60a5fa" strokeWidth="2" />
      <path d={path(rev, me - 1, 11)} fill="none" stroke="#60a5fa" strokeWidth="2" strokeDasharray="6 5" opacity=".75" />
      {/* margine */}
      <path d={path(marg, 0, me - 1)} fill="none" stroke="#34d399" strokeWidth="2.5" />
      <path d={path(marg, me - 1, 11)} fill="none" stroke="#34d399" strokeWidth="2.5" strokeDasharray="6 5" opacity=".75" />
      {marg.map((v, i) => (
        <circle key={i} cx={x(i)} cy={y(v)} r={i < me ? 3 : 2.5} fill={i < me ? "#34d399" : "#0f172a"}
          stroke="#34d399" strokeWidth={i < me ? 0 : 1.5} />
      ))}
      {MESI.map((m, i) => (
        <text key={m} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10"
          fill={i < me ? "#cbd5e1" : "#64748b"} fontStyle={i < me ? "normal" : "italic"}>{m}</text>
      ))}
    </svg>
  );
}

export default function MarginInsights() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = (force = false) => {
    if (force) setRefreshing(true);
    api.get(`/invoices/insights${force ? "?force=1" : ""}`)
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e.response?.data?.detail || "Analisi non disponibile"))
      .finally(() => setRefreshing(false));
  };
  useEffect(() => { load(); }, []);

  if (err) return <div className="panel"><p className="muted">📉 {err}</p></div>;
  if (!data) return <div className="panel"><p className="muted">📉 Preparo l'analisi predittiva…</p></div>;

  const { chart, months_elapsed: me, year_end: ye, ai } = data;
  const target = ye.target_margin;
  return (
    <div className="panel" data-testid="margin-insights">
      <div className="toolbar" style={{ marginTop: 0 }}>
        <h3 style={{ margin: 0 }}>📉 Analisi predittiva {data.year}</h3>
        <span className="muted" style={{ fontSize: 12 }}>
          linea piena = dati reali · tratteggiata = previsione
        </span>
        <div className="spacer" />
        <span style={{ fontSize: 12 }}><span style={{ color: "#34d399" }}>●</span> Margine</span>
        <span style={{ fontSize: 12 }}><span style={{ color: "#60a5fa" }}>●</span> Fatturato</span>
      </div>
      <Chart chart={chart} me={me} />
      <div className="dash-cards" style={{ marginTop: 8 }}>
        <div className="dash-card" data-testid="ins-yearend-margin">
          <div className="num" style={{ fontSize: 22, color: target && ye.margin < target ? "var(--danger)" : "var(--green)" }}>
            € {Math.round(ye.margin).toLocaleString("it-IT")}
          </div>
          <div className="lbl">Margine previsto a fine anno{target ? ` · obiettivo € ${Math.round(target).toLocaleString("it-IT")}` : ""}</div>
        </div>
        <div className="dash-card" data-testid="ins-yearend-revenue">
          <div className="num" style={{ fontSize: 22 }}>€ {Math.round(ye.revenue).toLocaleString("it-IT")}</div>
          <div className="lbl">Fatturato previsto a fine anno</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginTop: 14 }}>
        <div>
          <h4 style={{ margin: "0 0 8px" }}>💡 AI Insight</h4>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6 }} data-testid="ai-insights-list">
            {ai.insights.map((t, i) => <li key={i} style={{ fontSize: 13.5, lineHeight: 1.45 }}>{t}</li>)}
          </ul>
        </div>
        <div>
          <h4 style={{ margin: "0 0 8px" }}>🛠 Azioni consigliate</h4>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6 }} data-testid="ai-actions-list">
            {ai.actions.map((t, i) => <li key={i} style={{ fontSize: 13.5, lineHeight: 1.45 }}>{t}</li>)}
          </ul>
        </div>
      </div>
      <div className="toolbar" style={{ marginTop: 12 }}>
        <span className="muted" style={{ fontSize: 11.5 }}>
          {ai.generated_at ? `Aggiornato ${fmtDT(ai.generated_at)}` : ""}{ai.model ? ` · ${ai.model}` : ""} · si rigenera da solo quando cambiano i numeri
        </span>
        <div className="spacer" />
        <button className="btn sm" onClick={() => load(true)} disabled={refreshing} data-testid="ai-insights-refresh">
          {refreshing ? "Rigenero…" : "🔄 Aggiorna analisi"}
        </button>
      </div>
    </div>
  );
}
